/**
 * Full-width section band — a coloured rectangle that hosts other
 * blocks via the {@code content} nested slot. Ported from
 * ThemeMaker's {@code blocks/layout/Band/Band.server.tsx}.
 *
 * <p>The renderer pre-renders the nested {@code content} slot before
 * this component runs, so the JSX is simply {@code {content}} — no
 * call into {@code useChildrenRenderer}.
 */
import type { CSSProperties, ReactNode } from 'react';
import { BlockRoot } from '../_shared/BlockRoot';

const bandBgMap: Record<string, string> = {
  none: 'transparent',
  surface: 'var(--color-surface-page, #f7f8fa)',
  card: 'var(--color-surface-card, #fff)',
  'brand-50': 'var(--color-brand-50, #f0f4ff)',
  'brand-500': 'var(--color-brand-500, #3b5bdb)',
  'brand-700': 'var(--color-brand-700, #1e3a8a)',
  'neutral-100': 'var(--color-neutral-100, #f3f4f6)',
  'neutral-900': 'var(--color-neutral-900, #0f1218)',
  // 'image' is special — handled inline so the gradient overlay
  // merges with the image URL.
  image: 'transparent',
};

const bandMinHeightMap: Record<string, string> = {
  none: 'auto',
  'screen-quarter': '25vh',
  'screen-half': '50vh',
  screen: '100vh',
};

export interface BandProps {
  background?: keyof typeof bandBgMap;
  /** Public URL of the background image. Used only when
   *  {@code background='image'}. */
  backgroundImage?: string;
  /** Darkens an image background so light text stays readable.
   *  0–1; default 0.4. */
  imageOverlay?: number;
  minHeight?: keyof typeof bandMinHeightMap;
  /** When the band itself is dark, child text needs to invert. Auto-true
   *  for {@code brand-500} / {@code brand-700} / {@code neutral-900} /
   *  image backgrounds; opt-in for others. */
  darkText?: boolean;
  /** Pre-rendered ReactNode — resolved by RenderOneBlock before this runs. */
  content?: ReactNode;
}

export function Band({
  background = 'none',
  backgroundImage,
  imageOverlay = 0.4,
  minHeight = 'none',
  darkText,
  content,
}: BandProps) {
  const isImage = background === 'image' && backgroundImage;
  const overlay = Math.max(0, Math.min(1, imageOverlay));

  const bg = isImage
    ? `linear-gradient(rgba(0,0,0,${overlay}), rgba(0,0,0,${overlay})), url("${backgroundImage}") center/cover`
    : (bandBgMap[background] ?? 'transparent');

  // Auto-invert text when the background is conspicuously dark. Editors
  // can still force {@code darkText=false} for a brand-500 background
  // that uses dark-on-brand styling (rare, but sometimes right).
  const autoDark =
    background === 'brand-500' ||
    background === 'brand-700' ||
    background === 'neutral-900' ||
    isImage;
  const inverted = darkText ?? autoDark;

  const style: CSSProperties = {
    background: bg,
    // Own stacking context so a nested BackgroundGlow (z-index:-1) sits
    // above this band's background yet behind its content.
    position: 'relative',
    isolation: 'isolate',
    minHeight: bandMinHeightMap[minHeight] ?? 'auto',
    color: inverted ? '#fff' : 'inherit',
    // Vertical padding so child blocks don't kiss the band edges.
    // Horizontal padding intentionally omitted — children's own
    // SectionShells handle horizontal layout.
    padding: 'var(--space-12, 48px) 0',
    // Flex column with center justification so a {@code min-height} band
    // centres content vertically when the content is shorter.
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  };

  return (
    <BlockRoot as="section" style={style}>
      {content}
    </BlockRoot>
  );
}
